/**
 * Device Limit Manager
 * Handles device registration, limits, and fingerprinting for licenses
 */

import type { DeviceInfo } from '$lib/types/subscription';
import type { DeviceLimitManager } from './interfaces';
import { sha256 } from '$lib/utils/crypto';

/**
 * Device storage entry
 */
interface DeviceStorageEntry {
  licenseKey: string;
  devices: DeviceInfo[];
  lastUpdated: number;
}

/**
 * Device fingerprint components
 */
interface DeviceFingerprint {
  screen: string;
  timezone: string;
  language: string;
  platform: string;
  vendor: string;
  memory?: string;
  cpuCores?: string;
  userAgent: string;
}

export class DeviceLimitManagerImpl implements DeviceLimitManager {
  private readonly STORAGE_PREFIX = 'device_info_';
  private readonly DEVICE_ID_KEY = 'current_device_id';
  private readonly FINGERPRINT_CACHE_KEY = 'device_fingerprint_cache';
  
  // Cache for device information
  private deviceCache = new Map<string, DeviceInfo[]>();
  private currentDeviceInfo: DeviceInfo | null = null;
  private currentFingerprint: string | null = null;

  constructor() {
    this.initializeDeviceInfo();
  }

  /**
   * Initialize device information
   */
  private async initializeDeviceInfo(): Promise<void> {
    try {
      // Generate or retrieve device fingerprint
      this.currentFingerprint = await this.generateDeviceFingerprint();
      
      // Get or create current device info
      this.currentDeviceInfo = await this.getCurrentDeviceInfo();
    } catch (error) {
      console.error('Failed to initialize device info:', error);
    }
  }

  /**
   * Get current device count for license
   */
  async getDeviceCount(licenseKey: string): Promise<number> {
    const devices = await this.getActiveDevices(licenseKey);
    return devices.length;
  }

  /**
   * Get active devices for license
   */
  async getActiveDevices(licenseKey: string): Promise<DeviceInfo[]> {
    // Check cache first
    const cached = this.deviceCache.get(licenseKey);
    if (cached) {
      return cached;
    }

    try {
      const storageKey = this.getStorageKey(licenseKey);
      const stored = localStorage.getItem(storageKey);
      
      if (!stored) {
        return [];
      }

      const entry: DeviceStorageEntry = JSON.parse(stored);
      
      // Convert date strings back to Date objects and filter active devices
      const devices = entry.devices.map(device => ({
        ...device,
        activatedAt: new Date(device.activatedAt),
        lastActiveAt: new Date(device.lastActiveAt)
      })).filter(device => this.isDeviceActive(device));

      // Update cache
      this.deviceCache.set(licenseKey, devices);
      
      return devices;
    } catch (error) {
      console.error(`Failed to get devices for license ${licenseKey}:`, error);
      return [];
    }
  }

  /**
   * Add device to license
   */
  async addDevice(licenseKey: string, deviceInfo: DeviceInfo): Promise<boolean> {
    try {
      const existingDevices = await this.getActiveDevices(licenseKey);
      
      // Check if device already exists (by deviceId)
      const existingDevice = existingDevices.find(d => d.deviceId === deviceInfo.deviceId);
      if (existingDevice) {
        // Update existing device info
        existingDevice.lastActiveAt = new Date();
        existingDevice.isCurrentDevice = deviceInfo.isCurrentDevice;
      } else {
        // Add new device
        existingDevices.push({
          ...deviceInfo,
          activatedAt: new Date(),
          lastActiveAt: new Date()
        });
      }

      // Save updated device list
      await this.saveDeviceList(licenseKey, existingDevices);
      
      // Update cache
      this.deviceCache.set(licenseKey, existingDevices);
      
      return true;
    } catch (error) {
      console.error(`Failed to add device to license ${licenseKey}:`, error);
      return false;
    }
  }

  /**
   * Remove device from license
   */
  async removeDevice(licenseKey: string, deviceId: string): Promise<boolean> {
    try {
      const existingDevices = await this.getActiveDevices(licenseKey);
      const updatedDevices = existingDevices.filter(d => d.deviceId !== deviceId);
      
      if (updatedDevices.length === existingDevices.length) {
        // Device not found
        return false;
      }

      // Save updated device list
      await this.saveDeviceList(licenseKey, updatedDevices);
      
      // Update cache
      this.deviceCache.set(licenseKey, updatedDevices);
      
      return true;
    } catch (error) {
      console.error(`Failed to remove device ${deviceId} from license ${licenseKey}:`, error);
      return false;
    }
  }

  /**
   * Check if device can be added
   */
  async canAddDevice(licenseKey: string, maxDevices: number): Promise<boolean> {
    const currentCount = await this.getDeviceCount(licenseKey);
    const currentDevice = await this.getCurrentDeviceInfo();
    
    // Check if current device is already registered
    const existingDevices = await this.getActiveDevices(licenseKey);
    const isCurrentDeviceRegistered = existingDevices.some(d => d.deviceId === currentDevice.deviceId);
    
    if (isCurrentDeviceRegistered) {
      return true; // Current device is already registered
    }
    
    return currentCount < maxDevices;
  }

  /**
   * Update device last active time
   */
  async updateDeviceActivity(licenseKey: string, deviceId: string): Promise<void> {
    try {
      const devices = await this.getActiveDevices(licenseKey);
      const device = devices.find(d => d.deviceId === deviceId);
      
      if (device) {
        device.lastActiveAt = new Date();
        await this.saveDeviceList(licenseKey, devices);
        this.deviceCache.set(licenseKey, devices);
      }
    } catch (error) {
      console.error(`Failed to update device activity for ${deviceId}:`, error);
    }
  }

  /**
   * Generate device fingerprint
   */
  async generateDeviceFingerprint(): Promise<string> {
    if (this.currentFingerprint) {
      return this.currentFingerprint;
    }

    try {
      // Check cached fingerprint first
      const cached = localStorage.getItem(this.FINGERPRINT_CACHE_KEY);
      if (cached) {
        const { fingerprint, timestamp } = JSON.parse(cached);
        // Use cached fingerprint if less than 7 days old
        if (Date.now() - timestamp < 7 * 24 * 60 * 60 * 1000) {
          this.currentFingerprint = fingerprint;
          return fingerprint;
        }
      }

      const fingerprint = await this.calculateDeviceFingerprint();
      
      // Cache the fingerprint
      localStorage.setItem(this.FINGERPRINT_CACHE_KEY, JSON.stringify({
        fingerprint,
        timestamp: Date.now()
      }));

      this.currentFingerprint = fingerprint;
      return fingerprint;
    } catch (error) {
      console.error('Failed to generate device fingerprint:', error);
      // Fallback to a simple timestamp-based ID
      const fallback = `fallback_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      this.currentFingerprint = fallback;
      return fallback;
    }
  }

  /**
   * Get current device information
   */
  async getCurrentDeviceInfo(): Promise<DeviceInfo> {
    if (this.currentDeviceInfo) {
      return this.currentDeviceInfo;
    }

    // Use the same device ID generation as ServerLicenseService for consistency
    let deviceId = localStorage.getItem('device_id');
    if (!deviceId) {
      // Generate simple device ID (compatible with server)
      const timestamp = Date.now().toString(36);
      const randomStr = Math.random().toString(36).substring(2, 15);
      const userAgent = navigator.userAgent;
      const platform = navigator.platform;
      
      // Create a simple hash
      const baseString = `${userAgent}-${platform}-${timestamp}`;
      const simpleHash = baseString.split('').reduce((acc, char) => {
        return ((acc << 5) - acc) + char.charCodeAt(0);
      }, 0).toString(36);
      
      deviceId = `${randomStr}-${simpleHash}-${timestamp}`;
      localStorage.setItem('device_id', deviceId);
    }

    const deviceName = this.generateDeviceName();
    const platform = this.getPlatformInfo();

    this.currentDeviceInfo = {
      deviceId,
      deviceName,
      platform,
      activatedAt: new Date(),
      lastActiveAt: new Date(),
      isCurrentDevice: true
    };

    return this.currentDeviceInfo;
  }

  /**
   * Private helper methods
   */

  private getStorageKey(licenseKey: string): string {
    return `${this.STORAGE_PREFIX}${licenseKey}`;
  }

  private async saveDeviceList(licenseKey: string, devices: DeviceInfo[]): Promise<void> {
    const entry: DeviceStorageEntry = {
      licenseKey,
      devices: devices.map(device => ({
        ...device,
        activatedAt: device.activatedAt,
        lastActiveAt: device.lastActiveAt
      })),
      lastUpdated: Date.now()
    };

    const storageKey = this.getStorageKey(licenseKey);
    localStorage.setItem(storageKey, JSON.stringify(entry));
  }

  private isDeviceActive(device: DeviceInfo): boolean {
    // Consider device active if it was last seen within the last 90 days
    const maxInactivityDays = 90;
    const maxInactivityMs = maxInactivityDays * 24 * 60 * 60 * 1000;
    const timeSinceLastActive = Date.now() - device.lastActiveAt.getTime();
    
    return timeSinceLastActive < maxInactivityMs;
  }

  private async calculateDeviceFingerprint(): Promise<string> {
    const fingerprint: DeviceFingerprint = {
      screen: `${screen.width}x${screen.height}x${screen.colorDepth}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language || navigator.languages?.[0] || 'unknown',
      platform: navigator.platform,
      vendor: navigator.vendor || 'unknown',
      userAgent: navigator.userAgent
    };

    // Add hardware info if available
    try {
      if ('deviceMemory' in navigator) {
        fingerprint.memory = (navigator as any).deviceMemory?.toString() || 'unknown';
      }
      if ('hardwareConcurrency' in navigator) {
        fingerprint.cpuCores = navigator.hardwareConcurrency?.toString() || 'unknown';
      }
    } catch (error) {
      // Hardware info not available - not critical
    }

    // Create hash of fingerprint components
    const fingerprintString = Object.values(fingerprint).join('|');
    
    // Use crypto utility with fallback for Safari
    const hashHex = await sha256(fingerprintString);
    
    return hashHex;
  }

  private generateDeviceName(): string {
    const platform = navigator.platform;
    const userAgent = navigator.userAgent;
    
    // Try to extract meaningful device name from user agent
    if (userAgent.includes('Mac')) {
      if (userAgent.includes('Intel')) {
        return 'Mac (Intel)';
      } else if (userAgent.includes('Apple Silicon')) {
        return 'Mac (Apple Silicon)';
      }
      return 'Mac';
    }
    
    if (userAgent.includes('Windows')) {
      return 'Windows PC';
    }
    
    if (userAgent.includes('Linux')) {
      return 'Linux Computer';
    }
    
    if (userAgent.includes('Chrome')) {
      return 'Chrome Browser';
    }
    
    if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
      return 'Safari Browser';
    }
    
    if (userAgent.includes('Firefox')) {
      return 'Firefox Browser';
    }
    
    // Fallback to platform
    return platform || 'Unknown Device';
  }

  private getPlatformInfo(): string {
    const userAgent = navigator.userAgent.toLowerCase();
    const platform = navigator.platform.toLowerCase();
    
    if (userAgent.includes('mac') || platform.includes('mac')) {
      return 'macOS';
    }
    
    if (userAgent.includes('win') || platform.includes('win')) {
      return 'Windows';
    }
    
    if (userAgent.includes('linux') || platform.includes('linux')) {
      return 'Linux';
    }
    
    if (userAgent.includes('android')) {
      return 'Android';
    }
    
    if (userAgent.includes('iphone') || userAgent.includes('ipad')) {
      return 'iOS';
    }
    
    return 'Unknown';
  }
}